// Trang test authentication và navigation
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { BookOpen, Target, Play, User, LogOut } from 'lucide-react';

const TestAuth: React.FC = () => {
  const { user, profile, isAuthenticated, isLoading, loginWithGoogle, logout } = useAuth();

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Test Authentication & Navigation
          </h1>

          {/* Authentication Status */}
          <div className="mb-8 p-6 bg-gray-50 rounded-xl">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Authentication Status</h2>
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-gray-600">Đang tải...</span>
              </div>
            ) : isAuthenticated ? (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-primary-200 flex items-center justify-center">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-full h-full object-cover rounded-full" />
                    ) : (
                      <User className="w-6 h-6 text-primary-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{user?.name}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                    {profile?.grade && (
                      <p className="text-xs text-primary-600">Lớp {profile.grade}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Đăng xuất</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-600">Chưa đăng nhập</p>
                <button
                  onClick={handleLogin}
                  className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span>Đăng nhập với Google</span>
                </button>
              </div>
            )}
          </div>

          {/* Navigation Links */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Navigation Links</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/"
                className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <BookOpen className="w-6 h-6 text-blue-600" />
                <span className="font-medium text-blue-800">Trang chủ</span>
              </Link>
              
              <Link
                to="/tong-hop?type=bai-hoc"
                className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <BookOpen className="w-6 h-6 text-green-600" />
                <span className="font-medium text-green-800">Bài học</span>
              </Link>
              
              <Link
                to="/tong-hop?type=trac-nghiem"
                className="flex items-center space-x-3 p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
              >
                <Target className="w-6 h-6 text-orange-600" />
                <span className="font-medium text-orange-800">Trắc nghiệm</span>
              </Link>
              
              <Link
                to="/tong-hop?type=mo-phong"
                className="flex items-center space-x-3 p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <Play className="w-6 h-6 text-purple-600" />
                <span className="font-medium text-purple-800">Mô phỏng</span>
              </Link>
              
              <Link
                to="/chon-lop"
                className="flex items-center space-x-3 p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <BookOpen className="w-6 h-6 text-indigo-600" />
                <span className="font-medium text-indigo-800">Chọn lớp</span>
              </Link>
            </div>
          </div>

          {/* Test Pages */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Test Pages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/demo/interactive"
                className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <Play className="w-6 h-6 text-yellow-600" />
                <span className="font-medium text-yellow-800">Interactive Demo</span>
              </Link>
              
              <Link
                to="/demo/oscillation"
                className="flex items-center space-x-3 p-4 bg-pink-50 rounded-lg hover:bg-pink-100 transition-colors"
              >
                <Play className="w-6 h-6 text-pink-600" />
                <span className="font-medium text-pink-800">Oscillation Demo</span>
              </Link>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Hướng dẫn test</h3>
            <ol className="list-decimal list-inside space-y-2 text-blue-700">
              <li>Click "Đăng nhập với Google" để test authentication</li>
              <li>Sau khi đăng nhập, thông tin user sẽ hiển thị</li>
              <li>Click các link navigation để test routing</li>
              <li>Click "Đăng xuất" để test logout</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestAuth;
